<template>
    <layout-authenticated>
        <v-breadcrumbs :items="breadcrumb"></v-breadcrumbs>
        <h1 class="mb-3">{{ $t('shopping_cart.shopping_cart') }}</h1>
        <h3 class="grey--text mb-3">{{ $t('shopping_cart.add_product_quickly') }}</h3>
        <add-product-quickly-component :user-id="userId" :warehouse-id="warehouseId"/>
        <div class="d-flex flex-wrap align-center justify-space-between mb-3">
            <h3 class="grey--text mr-3 my-1">{{ $t('shopping_cart.products_in_cart') }}</h3>
            <v-btn outlined
                   small
                   color="error"
                   class="my-1"
                   :disabled="!canClearProducts"
                   @click="onClearProductsFromCart"
            >
                <v-icon left>mdi-trash-can</v-icon>
                {{ $t('shopping_cart.clear_products') }}
            </v-btn>
        </div>
        <div v-if="isShoppingCartEmpty">
            <v-alert icon="mdi-information" prominent text type="info">
                {{ $t('shopping_cart.shopping_cart_empty') }}
            </v-alert>
        </div>
        <shopping-cart-component v-else :user-id="userId" :warehouse-id="warehouseId" class="mb-3"/>
        <h3 class="grey--text mb-3">{{ $t('shopping_cart.add_recipient') }}</h3>
        <add-recipient-component :user-id="userId" :warehouse-id="warehouseId" allow-create/>
        <h3 class="grey--text mb-3">{{ $t('shopping_cart.recipients') }}</h3>
        <recipients-table @isValid="onIsValid" :user-id="userId" :warehouse-id="warehouseId"/>
        <div class="d-flex mt-3">
            <v-btn outlined
                   color="primary"
                   :disabled="recipientsCount === 0 || productsCount === 0 || !checkProductsCount || !recipientsValid"
                   @click="onAddOrderToBulkOrder"
            >
                <v-icon left>mdi-checkbox-marked-circle-plus-outline</v-icon>
                {{ $t('shopping_cart.add_order_to_bulk_order') }}
            </v-btn>
            <v-spacer/>
            <v-btn :to="{name: 'summary'}"
                   :disabled="recipientsCount === 0 || productsCount === 0 || !checkProductsCount || !recipientsValid"
                   color="primary"
                   class="mr-3"
            >
                <v-icon left>mdi-checkbox-marked-circle-outline</v-icon>
                {{ $t('shopping_cart.go_to_summary') }}
            </v-btn>
        </div>
    </layout-authenticated>
</template>

<script>
import ShoppingCartMixin from "@/mixins/ShoppingCartMixin";
import SnackbarMixin from "@/mixins/SnackbarMixin";
import BulkOrderMixin from '@/mixins/BulkOrderMixin'
import AddProductQuicklyComponent from '@/components/user/shoppingCart/AddProductQuicklyComponent'
import AddRecipientComponent from '@/components/user/shoppingCart/AddRecipientComponent'
import RecipientsTable from '@/components/user/recipients/RecipientsTable'
import ShoppingCartComponent from '@/components/user/shoppingCart/ShoppingCartComponent'
import LayoutAuthenticated from "@/layouts/LayoutAuthenticated";
import { EventBus } from "@/eventBus";

export default {
    name: 'ShoppingCart',
    mixins: [ShoppingCartMixin, SnackbarMixin, BulkOrderMixin],
    components: {
        LayoutAuthenticated,
        ShoppingCartComponent,
        RecipientsTable,
        AddRecipientComponent,
        AddProductQuicklyComponent,
    },
    data: () => ({
        recipientsValid: false
    }),
    computed: {
        userId() {
            return this.$store.getters['identity/getUserId'];
        },
        warehouseId() {
            return this.$store.getters['warehouse/getWarehouseId'](this.userId);
        },
        isShoppingCartEmpty() {
            return this.getNumberOfProductsInCart(this.userId, this.warehouseId) === 0;
        },
        canClearProducts() {
            return this.userId !== null && this.warehouseId !== null && !this.isShoppingCartEmpty;
        },
        recipientsCount() {
            return this.getRecipientsInCart(this.userId, this.warehouseId).length;
        },
        productsCount() {
            return this.getProductsInCart(this.userId, this.warehouseId).length;
        },
        checkProductsCount() {
            return this.getProductsInCart(this.userId, this.warehouseId)
                .reduce((carry, product) => carry && product.quantity > 0 && product.quantity <= product.in_stock, true);
        },
        breadcrumb() {
            return [
                {
                    text: this.$t('app.name'),
                    href: '/'
                },
                {
                    text: this.$t('shopping_cart.shopping_cart'),
                    href: '/cart',
                    disabled: true
                }
            ];
        }
    },
    methods: {
        onIsValid(valid) {
            this.recipientsValid = valid;
        },
        onClearProductsFromCart() {
            if (!this.canClearProducts || !confirm(this.$t('confirm.clear_products_from_shopping_cart'))) {
                return;
            }

            this.$store.commit('shoppingCart/clearProductsFromCart', {
                userId: this.userId,
                warehouseId: this.warehouseId
            });
            EventBus.$emit('refresh-shopping-cart-indicator');
        },
        onAddOrderToBulkOrder() {
            this.addOrderToBulkOrders(this.userId, this.warehouseId, this.getProductsInCart(this.userId, this.warehouseId), this.getRecipientsInCart(this.userId, this.warehouseId), "");
            this.removeProductsFromCart(this.userId, this.warehouseId); // it will remove recipients too

            this.snackbarSuccess(this.$t('snackbar.bulk_order.order_added_to_bulk_orders'));
            this.$router.push({name: 'bulk-orders'});
        }
    }
}
</script>
